/**
 * ttAuthAdapter.web.ts
 *
 * Adapts the TTAuth web service API to the same interface as
 * azureCiamService.web.ts so that authProvider.web.ts can delegate to
 * either provider without any changes to consumer code.
 *
 * Exported names match azureCiamService.web.ts exactly:
 *   - signinWithBrowser
 *   - refreshAccessToken
 *   - signoutWithBrowser
 *   - userManager  (with getUser(), events.addUserLoaded())
 */

import { ttAuthService } from './ttAuthService.web';
import { storageService } from '../../../shared/services/storageService';
import { authStorage } from '../api/authStorage';

// ---------------------------------------------------------------------------
// signinWithBrowser
// ---------------------------------------------------------------------------
/**
 * Initiates the TTAuth hosted-UI login redirect.
 * On web the redirect happens in the same tab, so this function never resolves
 * normally (the page navigates away).  We return a never-resolving promise so
 * TypeScript is happy and the call site matches the Entra signature.
 */
export const signinWithBrowser = (): Promise<{ accessToken: string }> => {
  // Use the plain origin (no path) as returnUrl so the TTAuth backend's
  // returnUrl allowlist is satisfied.  After Google auth, TTAuth sets the
  // HttpOnly session cookie and redirects to the origin root ('/').
  // AppNavigator.web.tsx's handleNavigationReady then detects the live cookie
  // via userManager.getUser() and navigates to ShopHomePage in-app.
  ttAuthService.loginRedirect(window.location.origin);
  return new Promise(() => { /* intentionally never resolves — page redirects */ });
};

// ---------------------------------------------------------------------------
// refreshAccessToken
// ---------------------------------------------------------------------------
/**
 * Silently refreshes the TTAuth cookie session via the /ttauth/v1/cookie/refresh
 * endpoint.  Returns the new bearer-equivalent session token on success, or
 * null if the session cannot be refreshed.
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  // NOTE: intentionally no top-level try/catch here.
  //
  // ttAuthService.refresh() propagates fetch/network errors as thrown exceptions
  // (not { ok: false }) so callers can distinguish:
  //   • { ok: false }  → server confirmed session dead  → return null (→ onSessionExpired)
  //   • thrown error   → network unreachable / timeout  → propagate to axiosInstance.ts
  //
  // axiosInstance.ts catch(refreshError) checks for TypeError / network message
  // and skips logout for transient connectivity failures, keeping the user logged in.
  // If we swallow the error here and return null, axiosInstance treats it as a
  // confirmed session expiry and calls onSessionExpired() — causing a false logout.
  const result = await ttAuthService.refresh();
  if (!result.ok) {
    // Server explicitly rejected the session — confirmed dead.
    return null;
  }
  if (result.token) {
    // Persist the real JWT so the axios interceptor sends it as Authorization: Bearer.
    await storageService.setAuthToken(result.token);
    console.log('[TTAuthAdapter.web] Token refreshed from /cookie/me and persisted');
    return result.token;
  }
  // Session alive but no token in body (cookie-only flow).
  // TTAuth proactively issues a fresh session cookie on this /cookie/me call,
  // so the session IS valid. Return the sentinel so the axios interceptor
  // knows to retry the original request using withCredentials (cookie) auth
  // rather than treating null as "session expired → logout".
  console.log('[TTAuthAdapter.web] Cookie session refreshed (cookie-only flow) — returning sentinel');
  await storageService.setAuthToken('ttauth-cookie-session');
  return 'ttauth-cookie-session';
};

// ---------------------------------------------------------------------------
// signoutWithBrowser
// ---------------------------------------------------------------------------
/**
 * Logs the user out via TTAuth cookie-session endpoint, then redirects.
 * Clears ALL local auth state (token, refresh token, secureStorage keys, force-reauth
 * flag, etc.) via authStorage.clear() BEFORE the page redirect fires, so that on
 * reload handleNavigationReady finds no token and stays on AgencySelectionScreen.
 *
 * Also sets a JUST_LOGGED_OUT sessionStorage flag so that handleNavigationReady's
 * userManager.getUser() fallback probe is skipped on the reloaded page — this
 * prevents a race where the server-side cookie is still valid when /cookie/me fires.
 */
export const signoutWithBrowser = async (): Promise<void> => {
  // authStorage.clear() removes: access token, refresh token, agency code, login hint,
  // sets forceReauth, clears all secureStorage keys (timezone, userId, shop, etc.).
  // Must complete before ttAuthService.logout() triggers the page redirect.
  await authStorage.clear();
  // Survives the page reload (unlike memory state). handleNavigationReady reads and
  // clears this flag to skip the userManager.getUser() /cookie/me probe.
  try {
    sessionStorage.setItem('JUST_LOGGED_OUT', '1');
  } catch {
    /* ignore — sessionStorage unavailable in some environments */
  }
  // Pass window.location.origin as returnUrl so the TTAuth hosted-UI logout page
  // uses the SPA origin as appUrl (must be in TTAuth's allowlist).
  // Without this the proxy-forwarded request uses the backend host as appUrl,
  // which is not in the allowlist → "Untrusted application" error.
  ttAuthService.logout(window.location.origin);
  return new Promise(() => { /* intentionally never resolves — page redirects */ });
};

// ---------------------------------------------------------------------------
// userManager (oidc-client-ts compatible stub)
// ---------------------------------------------------------------------------
/**
 * Provides the subset of the oidc-client-ts UserManager interface that is
 * consumed by AppNavigator.web.tsx and AgencySelectionScreen.tsx.
 *
 *  - getUser()            → probes /ttauth/v1/cookie/session to return a
 *                           User-compatible object, or null when unauthenticated.
 *  - signinRedirect()     → delegates to ttAuthService.loginRedirect()
 *  - signoutRedirect()    → delegates to ttAuthService.logout()
 *  - events.addUserLoaded → no-op stub (TTAuth uses cookie; no client event needed)
 */
export const userManager = {
  /**
   * Probes the live TTAuth cookie session via GET /ttauth/v1/cookie/me.
   * When the backend returns a real JWT in the body, persists it to storageService
   * so the axios interceptor sends it as Authorization: Bearer.
   * When the backend only sets a cookie (no body token), returns a sentinel so
   * AppNavigator still navigates to ShopHomePage — auth is then driven by the
   * HttpOnly cookie via withCredentials:true.
   */
  getUser: async (): Promise<{
    access_token: string;
    expires_at: number;
    profile: Record<string, unknown>;
  } | null> => {
    // NOTE: intentionally no try/catch here.
    //
    // ttAuthService.refresh() now propagates fetch/network errors as exceptions
    // (not { ok: false }) so callers can distinguish:
    //   • { ok: false }  → server confirmed session dead  → return null (force re-login)
    //   • thrown error   → network unreachable / timeout  → let it propagate
    //
    // AppNavigator.web.tsx bootstrap wraps this call in a try/catch labelled
    // "Non-blocking: leave whatever token is in storage as-is". A thrown
    // network error reaches that outer catch and the stored token is preserved,
    // preventing false logouts caused by transient connectivity issues.
    //
    // If the session is genuinely dead (server returns non-2xx), refresh() returns
    // { ok: false }, we return null, and bootstrap clears the stale token correctly.

    // refresh() calls GET /ttauth/v1/cookie/me (credentials: 'include').
    // Returns { ok, token } — ok=true when session is alive.
    // Throws on network/fetch error — propagates to AppNavigator bootstrap.
    const result = await ttAuthService.refresh();
    if (!result.ok) {
      // Server explicitly rejected — session is confirmed dead.
      return null;
    }
    let accessToken: string;
    if (result.token) {
      // Real JWT from response body — persist so axios sends it as Bearer.
      await storageService.setAuthToken(result.token);
      console.log('[TTAuthAdapter.web] TTAuth session alive — real JWT persisted for Bearer auth');
      accessToken = result.token;
    } else {
      // Cookie-only flow: session is alive but no body token.
      // Use sentinel so AppNavigator navigates to ShopHomePage;
      // BFF requests rely on withCredentials:true cookie.
      console.log('[TTAuthAdapter.web] TTAuth cookie session alive (cookie-only flow)');
      accessToken = 'ttauth-cookie-session';
    }
    return {
      access_token: accessToken,
      // Far-future epoch so `expires_at > now` always passes.
      expires_at: Math.floor(Date.now() / 1000) + 3600,
      profile: {},
    };
  },

  signinRedirect: (_args?: unknown): Promise<void> => {
    ttAuthService.loginRedirect();
    return new Promise(() => { /* never resolves */ });
  },

  signoutRedirect: (): Promise<void> => {
    ttAuthService.logout(window.location.origin);
    return new Promise(() => { /* never resolves */ });
  },

  events: {
    addUserLoaded:       (_cb: unknown) => { /* no-op — TTAuth does not fire client events */ },
    addUserUnloaded:     (_cb: unknown) => { /* no-op */ },
    addAccessTokenExpiring: (_cb: unknown) => { /* no-op */ },
    removeUserLoaded:    (_cb: unknown) => { /* no-op */ },
  },
};